// Shared helpers for rendering the CSI report card (used by the user result
// page and the admin/coach responses view).

// Row / box tint by rating label. Check "above" before "average" because
// "Above Average" contains the substring "average".
export function tone(rating) {
    const r = (rating || '').toLowerCase();
    if (r.includes('above') || r.includes('high-potential') || r.includes('high potential')) return 'green';
    if (r.includes('needs improvement') || r.includes('emerging')) return 'red';
    if (r.includes('average') || r.includes('developing')) return 'yellow';
    return 'blue'; // Needs More Information / Insufficient
}

// "green" here means the positive/success rating band — kept as the
// semantic success color (not the brand primary blue), since "blue" is
// already a distinct rating meaning above ("Needs More Information").
export const ROW_LIGHT = { green: 'bg-success-tint', yellow: 'bg-amber-100', red: 'bg-red-100', blue: 'bg-blue-50' };
export const ROW_DARK = { green: 'bg-success/20', yellow: 'bg-amber-900/30', red: 'bg-red-900/30', blue: 'bg-slate-700/40' };

// Friendlier display label for a rating (frontend only); the stored Ranking
// text is unchanged. Softens the tone on the user's result page so it reads as
// coaching, not a grade. (Admin/coach views keep the raw labels.)
export function displayRating(rating) {
    switch (tone(rating)) {
        case 'green': return 'Strength';
        case 'yellow': return 'Developing';
        case 'red': return 'Growth area';
        default: return 'Tell us more'; // blue / Needs More Information
    }
}

// Encouraging one-liner for the overall result, keyed off its rating's tone.
export function overallHeadline(rating) {
    switch (tone(rating)) {
        case 'green': return "You're thriving. Keep building on real momentum.";
        case 'yellow': return "You're growing steadily. A few focused moves go a long way.";
        case 'red': return "You're building your foundation. It's a strong place to start from.";
        default: return "Let's fill in a fuller picture of where you are.";
    }
}

// Display helper: space out numeric ranges ("0-1" -> "0 - 1", "6-12 months"
// -> "6 - 12 months") so they don't read as cramped. Stored value is unchanged.
export function formatChoice(s) {
    return String(s ?? '').replace(/(\d)-(\d)/g, '$1 - $2');
}

// Fixed spec order: Performance, Skilling, Networking, Community, Feedback.
export const CATEGORY_ORDER = [2, 3, 5, 4, 6];
export function orderResults(results) {
    const pos = id => { const i = CATEGORY_ORDER.indexOf(id); return i === -1 ? 999 : i; };
    return [...(results || [])].sort((a, b) => pos(a.focusAreaId) - pos(b.focusAreaId));
}

// Top Strengths / Growth Opportunity / Next Action from the category results.
// Strengths are the areas rated a strength (green); focus areas are the ones
// genuinely developing or weak (yellow + red). We never invent a "weakest" by
// list order when every area is already strong -- callers hide the section (or
// show a placeholder) when the set is empty.
export function deriveSummary(rows, overall) {
    const strengths = rows.filter(r => tone(r.rating) === 'green').map(r => r.focusAreaName);
    const focusRows = rows.filter(r => ['yellow', 'red'].includes(tone(r.rating)));
    const growth = focusRows.map(r => r.focusAreaName);
    // Primary focus = lowest-scoring focus area, or null when there is none.
    const growthRow = [...focusRows].sort((a, b) => (a.percent ?? 101) - (b.percent ?? 101))[0] || null;
    const nextAction = (growthRow && growthRow.recommendedAction) || (overall && overall.recommendedAction) || '—';
    return { strengths, growth, focusRows, growthRow, nextAction };
}
